import { forwardRef, useMemo, type ReactNode } from "react";
import { Chart, useChartTheme, resolveChartColor, usePrefersReducedMotion, commonStyles, commonTooltip, commonAxis, commonLegend, type ChartHandle, type ChartProps } from "./Chart";

// Stage 6: local replacement for @datasirpi/ui's <AreaChart>. The vendor's
// AreaChart is literally `<LineChart series={series.map(s => ({...s,
// area: true}))} />` — a thin forced-area wrapper around its own
// (otherwise identical, and never separately imported anywhere in this
// app) `<LineChart>`. Rather than expose an unused generic `LineChart`
// alongside it, this folds LineChart's exact option-building logic in
// here directly with `area` always forced on each series, matching the
// vendor's real behavior with no separate public surface nothing uses.
export interface AreaSeries<Row> {
  name: string;
  value: (row: Row) => number;
  color?: string;
  smooth?: boolean;
  stack?: string;
}

export interface AreaChartProps<Row> extends Omit<ChartProps, "option" | "tabularData"> {
  data: readonly Row[];
  xKey: (row: Row) => string | number;
  series: ReadonlyArray<AreaSeries<Row>>;
  showLegend?: boolean;
  showTooltip?: boolean;
  yAxisFormat?: (value: number) => string;
  tableCaption?: string;
  /**
   * Draw a point marker at every datum. Default true, which is right for a
   * dozen readings and wrong for a dense series — at 24+ points the markers
   * merge into a dotted band that reads as noise on top of the line it is
   * meant to describe. Turning them off keeps hover tooltips working.
   */
  showSymbols?: boolean;
  /** Fade the area fill to transparent at the baseline instead of a flat wash. */
  gradient?: boolean;
  /**
   * Value-axis scale. "log" keeps quiet periods readable next to bursts that
   * are 10–100× larger; zero readings have no position on a log axis and are
   * left as gaps rather than drawn at an invented value.
   */
  yScale?: "linear" | "log";
}

const compactFormat = new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 });
const compactNumber = (v: number) => compactFormat.format(v);

function AreaChartInner<Row>(
  { data, xKey, series, showLegend, showTooltip = true, yAxisFormat, tableCaption, showSymbols = true, gradient = false, yScale = "linear", ...rest }: AreaChartProps<Row>,
  ref: React.ForwardedRef<ChartHandle>
) {
  const theme = useChartTheme();
  const reducedMotion = usePrefersReducedMotion();

  const option = useMemo(() => {
    const xs = data.map(xKey);
    const isLegendShown = showLegend ?? series.length > 1;
    return {
      ...commonStyles(theme, { reducedMotion }),
      grid: { top: 24, right: 16, bottom: isLegendShown ? 56 : 24, left: 48 },
      tooltip: showTooltip ? { trigger: "axis", ...commonTooltip(theme) } : { show: false },
      legend: isLegendShown ? { show: true, data: series.map((s) => s.name), ...commonLegend(theme) } : { show: false },
      xAxis: { type: "category", boundaryGap: false, data: xs, ...commonAxis(theme) },
      yAxis: {
        ...(yScale === "log" ? { type: "log", logBase: 10, min: 1 } : { type: "value" }),
        ...commonAxis(theme),
        // A log axis spans powers of ten; full digits ("100,000") overflow the
        // 48px gutter and were clipped, so it labels compactly (1K, 100K).
        axisLabel: { ...commonAxis(theme).axisLabel, ...(yAxisFormat ? { formatter: yAxisFormat } : yScale === "log" ? { formatter: compactNumber } : {}) },
      },
      series: series.map((s, i) => {
        const color = resolveChartColor(s.color, theme.palette[i % theme.palette.length]);
        return {
          name: s.name,
          type: "line",
          smooth: s.smooth,
          stack: s.stack,
          symbolSize: 6,
          showSymbol: showSymbols,
          data: data.map((row) => {
            const v = s.value(row);
            return yScale === "log" && !(v > 0) ? null : v;
          }),
          itemStyle: { color },
          lineStyle: { width: 2 },
          // Phase 2.4 — flat fill, no gradient. The system forbids gradients
          // outright, and a fade-to-transparent also makes the same series
          // look like two different weights depending on where you read it.
          // `gradient` is kept in the prop surface so existing call sites
          // still compile; it now only chooses the fill's strength.
          areaStyle: { color, opacity: gradient ? 0.16 : 0.12 },
        };
      }),
    };
  }, [data, xKey, series, theme, reducedMotion, showTooltip, showLegend, yAxisFormat, showSymbols, gradient, yScale]);

  const tabularData = useMemo(
    () => ({
      ...(tableCaption ? { caption: tableCaption } : {}),
      headers: ["x", ...series.map((s) => s.name)] as ReactNode[],
      rows: data.map((row) => [String(xKey(row)), ...series.map((s) => String(s.value(row)))]),
    }),
    [data, xKey, series, tableCaption]
  );

  return <Chart ref={ref} option={option} tabularData={tabularData} {...rest} />;
}

export const AreaChart = forwardRef(AreaChartInner) as <Row>(
  props: AreaChartProps<Row> & { ref?: React.ForwardedRef<ChartHandle> }
) => ReturnType<typeof AreaChartInner>;
